import React, { useContext, useEffect, useRef, useState } from "react";
import "./data-exchange-add-comment.component.scoped.scss";
import { Box, makeStyles } from "@material-ui/core";
import axios from "axios";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTranslation } from "react-i18next";
import {
  TextField,
  InputAdornment,
  withStyles,
  Tooltip,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DoneIcon from "@material-ui/icons/Done";
import ClearIcon from "@material-ui/icons/Clear";
import { GlobalContext } from "../../../../../../store";
import { isValidLength, showPopupToastMsg } from "../../../../../../helpers";
import {
  getFileInfo,
  validateFiles,
} from "../../../../../../helpers/validate-file";
import { fileTypes } from "../../../../../../statics/file-types";
import { icons } from "../../../../../../statics/icons";
import { DataExchangeService } from "../../../../../../services/data-exchange.services";
import { LookupsService } from "../../../../../../services/lookups/lookups.service";

const maxChar = 500;
const maxFiles = 5;

type FileProgress = number[];
type UploadedFile = {
  name: string;
  type: string;
  size: string;
  url: string;
};

const StyledTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: "var(--primary-color)",
    color: "#fff",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

const TextFieldStyle = makeStyles((theme) => ({
  root: {
    "& .MuiInputBase-root": {
      backgroundColor: "#F9F9F9 !important", // Change this to your desired color
    },
  },
}));

const DataExchangeAddCommentComponent = (props: any) => {
  const { onAddedComment, droppedFiles } = props;
  const { t } = useTranslation();
  const dataExchangeService = new DataExchangeService();
  const lookupsService = new LookupsService();

  const textFieldStyle = TextFieldStyle();
  const inputFileRef = useRef(null);
  const uploadEl = useRef(null);
  const { user } = useContext(GlobalContext);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [filesListProgress, setFilesListProgress] = useState<any>([]);
  const [filesList, setFilesList] = useState<any>([]);
  const [filesListCanceled, setFilesListCanceled] = useState<any>([]);

  const clickUpload = () => {
    if (!saving) {
      const element: any = inputFileRef.current;
      element.click();
    }
  };

  const onFileChange = async (e: any) => {
    await onValidateFile(e.target.files);
    const element: any = inputFileRef.current;
    element.value = "";
  };

  const submit = async () => {
    if (!canSubmit()) return;
    setSaving(true);

    try {
      const uploadedFiles = filesList.length
        ? await uploadFilesWithProgress(filesList)
        : [];

      await saveCommentWithFiles(uploadedFiles);
    } catch (error) {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = (): boolean => {
    return (
      !saving &&
      isValidLength(comment, maxChar) &&
      filesList.length <= maxFiles &&
      Boolean(comment)
    );
  };

  const uploadFilesWithProgress = async (
    files: File[],
  ): Promise<UploadedFile[]> => {
    const progress: FileProgress = Array(files.length).fill(0);
    const canceledFiles: File[] = [];
    const uploadedFiles: UploadedFile[] = [];

    await Promise.all(
      files.map((file, index) =>
        uploadSingleFile(file, index, progress, uploadedFiles, canceledFiles),
      ),
    );

    if (canceledFiles.length) {
      setFilesListCanceled(canceledFiles);
    }

    return uploadedFiles;
  };

  const uploadSingleFile = async (
    file: File,
    index: number,
    progress: FileProgress,
    uploadedFiles: UploadedFile[],
    canceledFiles: File[],
  ) => {
    try {
      const { signedRequest, url } = await getSignedUrl(file);

      const cancelTokenSource = axios.CancelToken.source();
      Object.assign(file as any, { cancelTokenSource, url });
      setFilesList((prev: any) => {
        const next = [...prev];
        next[index] = file; // same File object, now augmented
        return next;
      });
      // filesList[index] = { ...filesList[index], cancelTokenSource, url };

      const uploadResponse = await lookupsService.uploadToSignedURL(
        signedRequest,
        filesList[index],
        (event: ProgressEvent) => {
          progress[index] = Math.round((100 * event.loaded) / event.total);
          setFilesListProgress([...filesListProgress, ...progress]);
        },
      );

      if (uploadResponse === -1) {
        canceledFiles.push(file);
        return;
      }

      uploadedFiles.push(formatFilePayload(file, url));
    } catch (error) {
      console.error(`Upload failed for ${file.name}:`, error);
    }
  };

  // ---------- Services ----------
  const getSignedUrl = (file: File) => {
    const name = file.name;
    return lookupsService.requestSignedUrl(name, "communications");
  };

  // ---------- Utils ----------
  const formatFilePayload = (file: File, url: string): UploadedFile => {
    const type = getFileInfo(file.type).fileType;
    const name = file.name.replace(new RegExp(`\\.${type}$`), "");
    const size = `${(file.size / 1024 / 1000).toFixed(2)} MB`;

    return {
      name,
      type,
      size,
      url,
    };
  };

  const saveCommentWithFiles = async (files: any[] = []) => {
    const result = await dataExchangeService.create({
      comment,
      files,
    });
    if (result.message === "SUCCESS") {
      onAddedComment(result.comment);
      resetData();
    } else {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
    setSaving(false);
  };

  const deleteFile = (index: number) => {
    const filesCopy = [...filesList];
    if (filesList[index].cancelTokenSource) {
      filesList[index].cancelTokenSource.cancel();
      filesList[index].canceled = true;
    } else {
      filesCopy.splice(index, 1);
      setFilesList(filesCopy);
    }
  };

  const onValidateFile = async (files: any) => {
    const filteredFiles = await validateFiles(files, fileTypes);
    if (filteredFiles && filteredFiles.length) {
      setFilesList([...filesList, ...filteredFiles]);
    }
  };

  const resetData = () => {
    setSaving(false);
    setComment("");
    setFilesList([]);
    setFilesListProgress([]);
  };

  const handleUploadFocus = (event: any) => {
    const element: any = uploadEl.current;
    element.classList.add("active");
  };

  const handleUploadBlur = (event: any) => {
    const element: any = uploadEl.current;
    element.classList.remove("active");
  };

  useEffect(() => {
    if (!saving && droppedFiles && droppedFiles.length) {
      onValidateFile(droppedFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFiles]);

  return (
    <Box className="w-100">
      <input
        type="file"
        ref={inputFileRef}
        onChange={onFileChange}
        style={{ display: "none" }}
        multiple
        accept={fileTypes}
        disabled={saving || user.role === "auditor"}
      />
      <TextField
        onFocus={handleUploadFocus}
        onBlur={handleUploadBlur}
        className="text-area-multiline upload-area w-100"
        id="timeline-file-upload"
        label={t("ADD_A_COMMENT_TO_THE_FILE")}
        multiline
        rows={3}
        variant="filled"
        value={comment}
        onChange={(e: any) => setComment(e.target.value)}
        disabled={saving || user.role === "auditor"}
        classes={{
          root: textFieldStyle.root,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <AttachFileIcon
                className="upload-icon mr-3"
                onClick={clickUpload}
              />
              <SendIcon
                className={
                  !saving &&
                  comment &&
                  isValidLength(comment, maxChar) &&
                  filesList.length <= maxFiles
                    ? "svg-active"
                    : "svg-grey"
                }
                onClick={submit}
              />
            </InputAdornment>
          ),
        }}
      />
      <div className="files-preview" ref={uploadEl}>
        {filesList.map((file: any, index: any) => {
          return (
            !file.canceled && (
              <Box
                className={"file-preview " + (saving ? "loading" : "")}
                key={index}
              >
                <StyledTooltip title={file.name} placement="bottom">
                  <img
                    src={
                      file.src
                        ? file.src
                        : icons[getFileInfo(file.type).fileType]
                    }
                    alt=""
                  />
                </StyledTooltip>
                {filesListProgress[index] !== 100 && (
                  <Box
                    className="file-delete"
                    onClick={() => deleteFile(index)}
                  >
                    <ClearIcon />
                  </Box>
                )}
                {filesListProgress[index] === 100 && (
                  <Box className="file-success">
                    <DoneIcon />
                  </Box>
                )}
                {saving && filesListProgress[index] < 100 && (
                  <Box className="file-progress">
                    {filesListProgress[index] > 5 ? (
                      <CircularProgress
                        variant="determinate"
                        value={filesListProgress[index]}
                      />
                    ) : (
                      <CircularProgress />
                    )}
                  </Box>
                )}
              </Box>
            )
          );
        })}
      </div>

      <Box className="d-flex align-items-center justify-content-between">
        <Box className="d-flex align-items-center">
          <Box
            className={
              "input-char-count mt-1 mr-1 " +
              (filesList.length > maxFiles ? "invalid" : "")
            }
          >
            {filesList.length - filesListCanceled.length}/{maxFiles}{" "}
            {t("files")}
          </Box>
          <Box className="input-char-count mt-1 mr-3">
            (PDF max. 20MB; JPG, PNG max. 8MB; MP4 max. 200MB)
          </Box>
        </Box>
        <Box
          className={
            "input-char-count mt-1 " +
            (comment && !isValidLength(comment, maxChar) ? "invalid" : "")
          }
        >
          {comment.length}/{maxChar} {t("character")}
        </Box>
      </Box>
    </Box>
  );
};

export default DataExchangeAddCommentComponent;
